/*
  Comportements de la fiche produit.

  Sans script la page reste complete : la galerie empile ses medias, les
  options sont de vrais champs de formulaire et le bouton d'ajout poste la
  variante affichee. Ce fichier n'ajoute que ce qui demande du calcul :

  1. la galerie ne montre qu'un media a la fois ;
  2. le pas de quantite ;
  3. la resolution de la variante choisie, qui met a jour ensemble
     l'identifiant poste, le prix, l'etat, l'action et le media
     (design.md §6.5 : ces elements changent ensemble ou ne changent pas).
*/
(() => {
  const root = document.querySelector('[data-product]');
  if (!root || typeof window.URLSearchParams !== 'function') return;

  document.documentElement.classList.add('product-enhanced');

  /* ---------- Galerie ---------- */

  const frames = [...root.querySelectorAll('[data-media-frame]')];
  const thumbs = [...root.querySelectorAll('[data-media-thumb]')];
  const mediaCurrent = root.querySelector('[data-media-current]');

  const prechargerMedia = (index) => {
    const frame = frames[index];
    const image = frame?.querySelector('[data-gallery-image]');
    if (!image || image.dataset.preloaded === 'true') return;
    image.dataset.preloaded = 'true';
    image.loading = 'eager';
    if (typeof image.decode === 'function') image.decode().catch(() => {});
  };

  const prechargerVoisins = (index) => {
    if (frames.length < 2) return;
    const lancer = () => {
      prechargerMedia((index + 1) % frames.length);
      prechargerMedia((index - 1 + frames.length) % frames.length);
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(lancer, { timeout: 900 });
    } else {
      window.setTimeout(lancer, 250);
    }
  };

  const montrerMedia = (id) => {
    if (!id) return;
    const cible = String(id);
    const indexCible = frames.findIndex((frame) => frame.dataset.mediaFrame === cible);
    if (indexCible < 0) return;
    prechargerMedia(indexCible);
    let trouve = false;
    let vignetteActive = null;
    let indexActif = 0;

    for (const [index, frame] of frames.entries()) {
      const actif = frame.dataset.mediaFrame === cible;
      frame.classList.toggle('is-active', actif);
      trouve = trouve || actif;
      if (actif) indexActif = index;
    }
    if (!trouve) return;

    for (const thumb of thumbs) {
      const actif = thumb.dataset.mediaThumb === cible;
      thumb.classList.toggle('is-active', actif);
      if (actif) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
      if (actif) vignetteActive = thumb;
    }

    if (mediaCurrent) mediaCurrent.textContent = String(indexActif + 1).padStart(2, '0');

    if (vignetteActive) {
      const rail = vignetteActive.closest('.product__thumbs');
      const cellule = vignetteActive.closest('.product__thumb') || vignetteActive;
      if (rail && typeof rail.scrollTo === 'function') {
        const reduit =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const comportement = reduit ? 'auto' : 'smooth';
        const rangee = window.getComputedStyle(rail).flexDirection === 'row';
        if (rangee) {
          const gauche = cellule.offsetLeft;
          const droite = gauche + cellule.offsetWidth;
          const vueGauche = rail.scrollLeft;
          const vueDroite = vueGauche + rail.clientWidth;
          if (gauche < vueGauche || droite > vueDroite) {
            rail.scrollTo({
              left: Math.max(0, gauche - (rail.clientWidth - cellule.offsetWidth) / 2),
              behavior: comportement,
            });
          }
        } else {
          const haut = cellule.offsetTop;
          const bas = haut + cellule.offsetHeight;
          const vueHaut = rail.scrollTop;
          const vueBas = vueHaut + rail.clientHeight;
          if (haut < vueHaut || bas > vueBas) {
            rail.scrollTo({
              top: Math.max(0, haut - (rail.clientHeight - cellule.offsetHeight) / 2),
              behavior: comportement,
            });
          }
        }
      }
    }

    prechargerVoisins(indexActif);
  };

  for (const thumb of thumbs) {
    thumb.addEventListener('click', (event) => {
      event.preventDefault();
      montrerMedia(thumb.dataset.mediaThumb);
    });
    const anticiper = () => {
      const index = frames.findIndex(
        (frame) => frame.dataset.mediaFrame === thumb.dataset.mediaThumb
      );
      if (index >= 0) prechargerMedia(index);
    };
    thumb.addEventListener('pointerenter', anticiper, { passive: true });
    thumb.addEventListener('focus', anticiper);
    thumb.addEventListener('touchstart', anticiper, { passive: true });
  }

  const indexInitial = frames.findIndex((frame) => frame.classList.contains('is-active'));
  if (indexInitial >= 0) prechargerVoisins(indexInitial);

  const changerMedia = (direction) => {
    if (frames.length < 2) return;
    const courant = frames.findIndex((frame) => frame.classList.contains('is-active'));
    const suivant = (Math.max(0, courant) + direction + frames.length) % frames.length;
    montrerMedia(frames[suivant].dataset.mediaFrame);
  };

  root.querySelector('[data-media-previous]')?.addEventListener('click', () => changerMedia(-1));
  root.querySelector('[data-media-next]')?.addEventListener('click', () => changerMedia(1));

  const stage = root.querySelector('.product__stage');
  stage?.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    changerMedia(event.key === 'ArrowLeft' ? -1 : 1);
  });

  /* ---------- Quantite ---------- */

  const quantite = root.querySelector('[data-quantity-input]');
  for (const bouton of root.querySelectorAll('[data-quantity-step]')) {
    bouton.addEventListener('click', () => {
      if (!quantite) return;
      const pas = Number(bouton.dataset.quantityStep);
      const valeur = Number(quantite.value) || 1;
      quantite.value = String(Math.max(1, valeur + pas));
      quantite.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Variantes ---------- */

  const source = root.querySelector('[data-product-variants]');
  const champId = root.querySelector('[data-variant-id]');
  const entrees = [...root.querySelectorAll('[data-option-input]')];
  if (!source || !champId || entrees.length === 0) return;

  let variantes = [];
  try {
    variantes = JSON.parse(source.textContent);
  } catch {
    return;
  }

  const prix = root.querySelector('.price__now, .price__pending');
  const etat = root.querySelector('[data-product-state]');
  const action = root.querySelector('[data-product-submit]');

  let libelles = {};
  try {
    libelles = JSON.parse(root.dataset.strings || '{}');
  } catch {
    libelles = {};
  }

  const etatDepart = etat ? { classe: etat.className, texte: etat.textContent.trim() } : null;
  const actionDepart = action ? action.textContent.trim() : '';

  /*
    Un bouton desactive doit dire pourquoi (design.md §6.3) : l'etat au-dessus
    porte l'explication, le bouton ne fait que refuser l'action.
  */
  const direEtat = (classe, texte) => {
    if (!etat || !etatDepart) return;
    etat.className = classe ? `product__state product__state--${classe}` : etatDepart.classe;
    etat.textContent = texte || etatDepart.texte;
  };

  const choixCourant = () => {
    const choix = [];
    for (const entree of entrees) {
      const position = Number(entree.dataset.optionInput);
      if (entree.type === 'radio' && !entree.checked) continue;
      choix[position - 1] = entree.value;
    }
    return choix;
  };

  const resoudre = (choix) =>
    variantes.find((variante) =>
      variante.options.every((valeur, index) => valeur === choix[index])
    );

  const nommerCouleur = (choix) => {
    for (const cible of root.querySelectorAll('[data-option-value]')) {
      const valeur = choix[Number(cible.dataset.optionValue) - 1];
      if (valeur) cible.textContent = valeur;
    }
  };

  const appliquer = () => {
    const choix = choixCourant();
    nommerCouleur(choix);

    const variante = resoudre(choix);

    /*
      Une combinaison inexistante ne doit pas laisser en place le prix de la
      precedente : mieux vaut dire qu'elle n'est pas offerte que montrer une
      valeur qui ne correspond plus a rien.
    */
    if (!variante) {
      champId.value = '';
      direEtat('sold-out', libelles.no_variant);
      if (action && action.tagName === 'BUTTON') {
        action.disabled = true;
        action.textContent = libelles.unavailable || actionDepart;
      }
      return;
    }

    champId.value = variante.id;
    if (prix && variante.price) prix.textContent = variante.price;
    if (variante.media) montrerMedia(variante.media);

    if (action && action.tagName === 'BUTTON') {
      action.disabled = !variante.available;
      action.textContent = variante.available
        ? actionDepart
        : libelles.unavailable || actionDepart;
    }
    direEtat(variante.available ? '' : 'sold-out', variante.available ? '' : libelles.sold_out);

    /*
      L'adresse doit representer la variante affichee : un lien partage doit
      rouvrir exactement ce qui etait a l'ecran.
    */
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variante.id);
    window.history.replaceState({}, '', url);
  };

  for (const entree of entrees) {
    entree.addEventListener('change', appliquer);
  }
})();
